import { EPropertyApplicability, EMasterPropertyCodes, EPropertyType } from '../data/Constants';
import {
  IProperty,
  ISelectedTreeItem,
  IFeature,
  IProductHierarchy,
  ILov,
  ILovDetail,
} from '../../types';
import { IPropAssociation } from '../../types/IRequestTypes';
import { current, isDraft } from '@reduxjs/toolkit';

//Formats the data to be sent in Property Association Request
export function getPropAssocRequest(material: ISelectedTreeItem): IPropAssociation {
  if (material.featureCode && material.productCode) {
    //Read and Save Associations Request for Options
    return {
      productCode: material.productCode,
      featureCode: material.featureCode,
      optionCode: material.code,
    };
  } else if (material.productCode) {
    //Read and Save Associations Request for Features
    return {
      productCode: material.productCode,
      featureCode: material.code,
    };
  } else {
    //Read and Save Associations Request for Product
    return {
      productCode: material.code,
    };
  }
}

// Removes keys with empty string or null values from an object
export function removeEmptyValues(obj: { [key: string]: string | null }) {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== '' && v != null));
}

/**
 * Checks if a property is Master
 * @param {IProperty} property The property to check
 * @returns {boolean} True if the property's applicability includes Master, false otherwise
 */
export function checkIfMasterProperty(property: IProperty): boolean {
  return property.applicability.includes(EPropertyApplicability.Master);
}

/**
 * This function maps the list of possible values available to enum type properties
 * Exceptional case: AGC property is converted from String to Enum type with product hierarchy as LOV
 *
 * @param {IProperty[]} properties Properties that contains the details of each property
 * @param {IProductHierarchy[]} productHierarchy Product hierarchy list used for AGCode dropdown
 * @param {ILov[]} listOfValues List of values for properties
 * @returns {IProperty[]} Properties data with LOV mappings
 */
export function preparePropertiesLov(
  properties: IProperty[],
  productHierarchy: IProductHierarchy[],
  listOfValues: ILov[]
): IProperty[] {
  const propertiesData = structuredClone(isDraft(properties) ? current(properties) : properties);
  propertiesData.forEach((prop: IProperty) => {
    // Exceptional case for AGC to show drop down.
    if (prop.code === EMasterPropertyCodes.AgCode && prop.type === EPropertyType.String) {
      prop.type = EPropertyType.Enum;
      prop.selectionType = EPropertyType.Single;
      const agcLov: ILovDetail[] = productHierarchy.map((product: IProductHierarchy) => ({
        code: product.ag,
        name: product.description,
      }));
      prop.listOfValues = agcLov || [];
    }
    if (prop.type === EPropertyType.Enum && prop.values) {
      const foundLov = listOfValues.find((lov: ILov) => lov.code === prop.values);
      prop.listOfValues = foundLov ? foundLov.options : [];
      prop.default = prop.default ? JSON.stringify([prop.default.code]) : null;
    }
  });
  return propertiesData;
}

/**
 * Check if PMP (Product Model Processor) property is active
 * @param {IFeature[]} features Features array to search through
 * @returns {boolean} True if PMP feature is active, false otherwise
 */
export function getPMPPropertyStatus(features: IFeature[]) {
  let isActiveProduct = false;
  features.forEach((feature: IFeature) => {
    if (feature.code === 'PMP') {
      isActiveProduct = feature.isActive;
    }
  });
  return isActiveProduct;
}
